import { ReactNode, useEffect, useState } from 'react';
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';
import { PayoutPerson } from '../../services/business/payoutService';

export const PARTNER_TYPES = [
  { value: 'driver', label: 'Driver' },
  { value: 'delivery_partner', label: 'Delivery partner' },
  { value: 'service_provider', label: 'Service provider' },
];

export const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
  pending: 'warning',
  approved: 'info',
  processing: 'primary',
  paid: 'success',
  verified: 'success',
  rejected: 'error',
  failed: 'error',
  disabled: 'default',
  cancelled: 'default',
  not_submitted: 'default',
};

export function titleCase(value?: string) {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function StatusChip({ status }: { status?: string }) {
  return <Chip size="small" label={titleCase(status)} color={STATUS_COLORS[status || ''] || 'default'} />;
}

export function personLabel(p?: PayoutPerson | null) {
  if (!p) return '—';
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || p.phone || p.email || '—';
}

export function dateText(value?: string) {
  return value ? new Date(value).toLocaleString() : '—';
}

export interface FilterState {
  search: string;
  userType: string;
  from: string;
  to: string;
}

export const EMPTY_FILTERS: FilterState = { search: '', userType: '', from: '', to: '' };

export function filterParams(f: FilterState) {
  return {
    search: f.search.trim() || undefined,
    userType: f.userType || undefined,
    from: f.from || undefined,
    to: f.to || undefined,
  };
}

/** Search + partner type + date range; `extra` renders additional controls inline. */
export function PayoutFilterBar({
  value, onChange, extra, hideSearch,
}: { value: FilterState; onChange: (next: FilterState) => void; extra?: ReactNode; hideSearch?: boolean }) {
  const [search, setSearch] = useState(value.search);
  useEffect(() => {
    const t = setTimeout(() => { if (search !== value.search) onChange({ ...value, search }); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} mb={2} flexWrap="wrap">
      {!hideSearch && (
        <TextField size="small" label="Search partner / name / phone" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
      )}
      <TextField select size="small" label="Partner type" value={value.userType} onChange={(e) => onChange({ ...value, userType: e.target.value })} sx={{ minWidth: 170 }}>
        <MenuItem value="">All partner types</MenuItem>
        {PARTNER_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
      </TextField>
      <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} />
      <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} />
      {extra}
    </Stack>
  );
}

export interface PromptField {
  name: string;
  label: string;
  required?: boolean;
  type?: 'text' | 'date' | 'select' | 'multiline';
  options?: { value: string; label: string }[];
  defaultValue?: string;
  minLength?: number;
}

/** Small form dialog used for reject / mark paid / mark failed reasons. */
export function PromptDialog({
  open, title, description, fields, confirmLabel, confirmColor = 'primary', busy, error, onClose, onSubmit,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  fields: PromptField[];
  confirmLabel: string;
  confirmColor?: 'primary' | 'error' | 'success' | 'warning';
  busy?: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (open) setValues(Object.fromEntries(fields.map((f) => [f.name, f.defaultValue || ''])));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalid = fields.some((f) => f.required && (values[f.name] || '').trim().length < (f.minLength ?? 1));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {description}
          {error && <Alert severity="error">{error}</Alert>}
          {fields.map((f) => (
            <TextField
              key={f.name}
              label={f.label}
              required={f.required}
              select={f.type === 'select'}
              type={f.type === 'date' ? 'date' : 'text'}
              multiline={f.type === 'multiline'}
              minRows={f.type === 'multiline' ? 2 : undefined}
              InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
              value={values[f.name] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
            >
              {f.options?.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color={confirmColor} disabled={busy || invalid} onClick={() => onSubmit(values)}>
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
